/**
 * Organization-level constants
 *
 * Centralized constants for organization settings that affect the entire app.
 * These are used by both client components and server-side validation.
 */

/**
 * Display name for system/automated actions in UI.
 * Used when showing actions performed automatically (no human actor).
 *
 * @example
 * // In timeline: "Status changed by Cadence"
 * const attribution = actor ? actor.name : SYSTEM_ACTOR_DISPLAY_NAME
 */
export const SYSTEM_ACTOR_DISPLAY_NAME = 'SchoolAI Admin'

/**
 * Available timezone options for the organization.
 * Used in Organization Settings UI and server-side validation.
 */
export const TIMEZONE_OPTIONS = [
  { value: 'America/Denver', label: 'Mountain Time (America/Denver)' },
  {
    value: 'America/Los_Angeles',
    label: 'Pacific Time (America/Los_Angeles)',
  },
  { value: 'America/Chicago', label: 'Central Time (America/Chicago)' },
  { value: 'America/New_York', label: 'Eastern Time (America/New_York)' },
  { value: 'UTC', label: 'UTC' },
] as const

/**
 * Valid timezone values for validation.
 * Extracted from TIMEZONE_OPTIONS for easy use in server actions.
 */
export const VALID_TIMEZONES = TIMEZONE_OPTIONS.map(tz => tz.value)

export type ValidTimezone = (typeof VALID_TIMEZONES)[number]

/**
 * Review Category Slugs
 *
 * Canonical slugs for built-in review categories.
 * Use these constants instead of hardcoding strings.
 *
 * @see Issue #2677 - Migration from legacy EOS models to unified ReviewCategory system
 */
export const EOS_SCORECARD_CATEGORY_SLUG = 'eos-scorecard'
export const CORE_VALUES_CATEGORY_SLUG = 'core-values'

/**
 * Business-hours window bounds for the calendar synopsis.
 *
 * MIN/MAX define the valid hour range admins can set in
 * `/admin/organization/features` (Business hours form). Both client-side
 * validation and the `updateOrgBusinessHours` server action import these
 * so the boundary is declared in exactly one place.
 *
 * DEFAULT_START / DEFAULT_END mirror the fallback used by
 * `src/lib/pulse-synopsis/calendar-preprocessing.ts` when the
 * `Organization.businessHoursStart` / `businessHoursEnd` columns are
 * NULL. The source of truth for the fallback lives in the
 * preprocessing module; mirrored here so the admin form's placeholders
 * and help text stay in sync if the fallback ever changes.
 */
export const BUSINESS_HOUR_MIN = 0
export const BUSINESS_HOUR_MAX = 23
export const BUSINESS_HOURS_DEFAULT_START = 8
export const BUSINESS_HOURS_DEFAULT_END = 18

/**
 * The org-local day on which the DEFAULT pulse week rolls over (#13958).
 *
 * Sunday-indexed (`0` = Sunday … `6` = Saturday) to match
 * `PulseReminderSchedule.dayOfWeek` and every other day-of-week vocabulary in
 * the codebase, so the two numbers an admin sees on adjacent pages mean the
 * same thing.
 *
 * Day only, and there is no companion time-of-day constant on purpose: the
 * rollover happens at org-local midnight. The setting this replaced carried
 * a `HH:MM`, which is why `/pulse` named one week at 08:00 and another at
 * 09:01 on the same morning for 50 people.
 *
 * `null` (unset) is a valid stored state meaning "not configured"; the
 * resolver then reproduces today's cadence-less behaviour exactly. The UI
 * renders that as the `NOT_CONFIGURED` option value below rather than as an
 * empty string, so an unset org is readable rather than blank.
 */
export const PULSE_WEEK_ROLLOVER_DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
] as const

/** The `<select>` value that means "clear the setting" (stores NULL). */
export const PULSE_WEEK_ROLLOVER_NOT_CONFIGURED = 'not-configured'

/**
 * The organization page's rollover value for an org whose weekly pulse
 * practice is set up (#15573): the practice owns the day weeks open on, set
 * on the Pulse Checks Schedule tab, and the org's day follows it — so the
 * page offers no control of its own.
 */
export const PULSE_WEEK_ROLLOVER_SET_BY_PRACTICE = 'set-by-weekly-practice'

/**
 * Whether `value` is a storable rollover day.
 *
 * Rejects non-integers and out-of-range numbers rather than clamping them: a
 * guessed day moves every person in the org onto a different week, and the
 * documented fallback (unset) is the safe direction.
 */
export function isPulseWeekRolloverDay(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 6
  )
}
